import React from "react";
import {
  clearSearchCache,
  fetchJson,
  PROFILE_ENDPOINT,
  API_HOST,
  ADMIN_PROGRESS_PREFIX,
} from "../utils";
import {
  SESSION_ERROR_EVENT,
  requestJson,
  describeApiError,
} from "../api/request";
import { removeStoredValuesWithPrefix } from "../utils/storage";

const anonymous = { username: "", role: "basic", status: "anonymous" };

function clearSessionCache() {
  clearSearchCache();
  removeStoredValuesWithPrefix(ADMIN_PROGRESS_PREFIX);
}

export default function useUserProfile(endpoint = PROFILE_ENDPOINT) {
  const [session, setSession] = React.useState({
    ...anonymous,
    status: "loading",
  });
  const [refreshing, setRefreshing] = React.useState(false);
  const [signingOut, setSigningOut] = React.useState(false);
  const [signOutError, setSignOutError] = React.useState("");
  const logoutPending = React.useRef(false);
  const generation = React.useRef(0);
  const identity = React.useRef("");

  const refreshProfile = React.useCallback(async () => {
    if (logoutPending.current) return;
    const request = ++generation.current;
    setRefreshing(true);
    let next;
    try {
      const data = await fetchJson(endpoint, { skipSessionRefresh: true });
      if (
        typeof data?.username !== "string" ||
        !data.username ||
        typeof data.role !== "string"
      ) {
        throw new Error("Invalid profile response");
      }
      next = {
        username: data.username,
        role: data.role,
        status: "authenticated",
        profileVersion: request,
      };
    } catch (error) {
      next =
        error.status === 401
          ? anonymous
          : { ...anonymous, status: "error", error };
    }
    if (request !== generation.current) return;
    const nextIdentity = `${next.username}:${next.role}`;
    if (identity.current !== nextIdentity) clearSessionCache();
    identity.current = nextIdentity;
    setSession((previous) =>
      next.status === "error" && previous.status === "authenticated"
        ? { ...previous, error: next.error }
        : next,
    );
    setRefreshing(false);
    return next;
  }, [endpoint]);

  const signOut = React.useCallback(async () => {
    if (logoutPending.current) return false;
    logoutPending.current = true;
    generation.current += 1;
    setRefreshing(false);
    setSigningOut(true);
    setSignOutError("");
    try {
      await requestJson(`${API_HOST}/auth/discogs/logout/`, {
        method: "POST",
        skipSessionRefresh: true,
      });
      generation.current += 1;
      identity.current = "";
      clearSessionCache();
      setSession(anonymous);
      return true;
    } catch (error) {
      setSignOutError(describeApiError(error));
      return false;
    } finally {
      logoutPending.current = false;
      setSigningOut(false);
    }
  }, []);

  React.useEffect(() => {
    refreshProfile();
    const handleFailure = (event) => {
      if (event.detail.status === 401) {
        generation.current += 1;
        clearSessionCache();
        setSession(anonymous);
        setRefreshing(false);
      } else {
        refreshProfile();
      }
    };
    const resume = () => {
      if (!document.hidden) refreshProfile();
    };
    window.addEventListener(SESSION_ERROR_EVENT, handleFailure);
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      generation.current += 1;
      window.removeEventListener(SESSION_ERROR_EVENT, handleFailure);
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [refreshProfile]);

  return {
    ...session,
    refreshing,
    refreshProfile,
    signOut,
    signingOut,
    signOutError,
  };
}
